import * as React from "react"
import { Textarea } from "@/components/ui/primitives"
import { useAutosizeTextarea, type UseAutosizeTextareaOptions } from "@/hooks/use-autosize-textarea"
import { cn } from "@/lib/utils"

export type AutosizeTextareaProps = React.ComponentProps<typeof Textarea> & UseAutosizeTextareaOptions

function assignRef<T>(ref: React.ForwardedRef<T>, value: T | null) {
  if (typeof ref === "function") ref(value)
  else if (ref) ref.current = value
}

export const AutosizeTextarea = React.forwardRef<HTMLTextAreaElement, AutosizeTextareaProps>(
  (
    { maxRows, minRows, minHeightPx, maxHeightPx, className, value, onChange, rows: _rows, style, ...props },
    forwardedRef,
  ) => {
    const stringValue = String(value ?? "")
    const { ref, resize } = useAutosizeTextarea(stringValue, { maxRows, minRows, minHeightPx, maxHeightPx })

    const setRef = (node: HTMLTextAreaElement | null) => {
      ref.current = node
      assignRef(forwardedRef, node)
    }

    return (
      <Textarea
        {...props}
        ref={setRef}
        value={value}
        rows={minRows ?? 1}
        style={{ resize: "none", ...style }}
        className={cn("min-h-0 overflow-hidden", className)}
        onChange={(event) => {
          onChange?.(event)
          resize()
        }}
      />
    )
  },
)
AutosizeTextarea.displayName = "AutosizeTextarea"
